/**
 * ResetPassword.js
 * Password reset form component (with token from email link)
 */

import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Container, Row, Col } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../../services/api';
import './Login.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setError('Token mancante. Usa il link ricevuto via email.');
    }
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setError('Token mancante');
      return;
    }

    if (!formData.newPassword) {
      setError('Password obbligatoria');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('La password deve essere di almeno 6 caratteri');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Le password non coincidono');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await authService.resetPassword(token, formData.newPassword);
      
      if (response.success) {
        setSuccess(true);
        // Redirect to returnUrl if present and safe (relative URL), otherwise to /login
        const returnUrl = searchParams.get('returnUrl');
        const safeReturnUrl = returnUrl && returnUrl.startsWith('/') ? returnUrl : '/login';
        const redirectState = safeReturnUrl === '/login'
          ? { state: { message: 'Password aggiornata con successo. Effettua il login.' } }
          : {};
        setTimeout(() => {
          navigate(safeReturnUrl, redirectState);
        }, 2000);
      } else {
        setError(response.message || 'Errore nel reset della password');
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Reset password error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="login-container">
      <Row className="justify-content-center align-items-center min-vh-100">
        <Col xs={12} sm={11} md={10} lg={8} xl={6} xxl={5}>
          <Card className="login-card">
            <div className="login-header">
              <h3 className="mb-0">
                <i className="fas fa-lock me-2"></i>
                Reimposta Password
              </h3>
              <p className="mb-0 mt-2">Inserisci la tua nuova password</p>
            </div>
            
            <Card.Body className="login-body">
              {error && (
                <Alert variant="danger" className="mb-3">
                  {error}
                  {error.includes('scaduto') && (
                    <div className="mt-2">
                      <Link to="/forgot-password" className="text-decoration-none">
                        Richiedi un nuovo link
                      </Link>
                    </div>
                  )}
                </Alert>
              )}

              {success && (
                <Alert variant="success" className="mb-3">
                  <strong>Password aggiornata con successo!</strong><br />
                  {searchParams.get('returnUrl')?.startsWith('/')
                    ? 'Verrai reindirizzato alla pagina di accesso al portale...'
                    : 'Verrai reindirizzato alla pagina di login...'}
                </Alert>
              )}
              
              {!success && token && (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nuova Password</Form.Label>
                    <Form.Control
                      type="password"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      placeholder="Inserisci la nuova password"
                      disabled={loading}
                      required
                      minLength={6}
                    />
                    <Form.Text className="text-muted">
                      Almeno 6 caratteri
                    </Form.Text>
                  </Form.Group>
                  
                  <Form.Group className="mb-4">
                    <Form.Label>Conferma Password</Form.Label>
                    <Form.Control
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Conferma la nuova password"
                      disabled={loading}
                      required
                      minLength={6}
                    />
                  </Form.Group>
                  
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-100 mb-3"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Aggiornamento in corso...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check me-2"></i>
                        Reimposta Password
                      </>
                    )}
                  </Button>
                </Form>
              )}

              <div className="text-center">
                <Link to="/login" className="text-decoration-none">
                  <i className="fas fa-arrow-left me-2"></i>
                  Torna al login
                </Link>
              </div>
              
              <div className="text-center mt-4">
                <small className="text-muted">
                  © 2025 Fitmanager - Sistema di gestione per associazioni sportive
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ResetPassword;
